import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LifeBuoyIcon, MessageSquareIcon, PlusIcon, SearchXIcon } from 'lucide-react'

import { alpha, Box, Fade, Stack, Typography, useTheme } from '@mui/material'

import { Button, Card } from '@repo/ui'
import {
  EmptyState,
  QueryErrorState,
  StatusChip,
  StickyFormHeader,
  SupportTypeBadge,
  SystemGeneratedBadge,
} from 'components'
import { DEFERRED_LOADING_FADE_MS, useDeferredLoading } from 'hooks'
import { useSupportPermissions, useSupportRequests } from 'hooks/api'
import { useSettingsStickyOffset } from 'pages/settings/SettingsPage/settingsStickyOffset'
import { ROUTES } from 'routes'
import { relativeOrDateTime } from 'utils'
import type { SupportRequest } from 'services/supportApi'

import { SupportListControlsSkeleton, SupportPageSkeleton } from './SupportPageSkeleton'
import { displayStatusKey, SUPPORT_DISPLAY_STATUSES } from '../displayStatus'
import {
  applySupportFilters,
  EMPTY_SUPPORT_FILTERS,
  SupportListControls,
  type SupportListFilters,
} from './SupportListControls'

interface SupportPageProps {
  /**
   * Whose requests to list. Comes from the ROUTE, not from in-page state.
   *
   * This used to be a `useState` pair driven by two chip buttons at the top of the page. The
   * scopes are now two settings sections — Personal → Support and Company → Support (VRG-221) —
   * which is a better fit for what they are: "mine" and "everyone's" are not two views of one
   * page, they are a personal thing and a company thing, and the app already has exactly that
   * split as its settings IA. Three things fall out of the move:
   *
   *   - The tab pair disappears. The left nav already says which one you are on, so the chips
   *     were a second wayfinding control saying the same thing.
   *   - The permission gate moves to where every other gated surface has it: the nav row is
   *     disabled with a tooltip naming the permission (§27), rather than a disabled chip inside
   *     a page you already reached.
   *   - Each scope becomes linkable and bookmarkable, which in-page state never was.
   */
  scope: 'mine' | 'company'
}

/**
 * A list of support requests at one scope (VRG-226 — C2, re-homed by VRG-221).
 *
 * Rendered as cards rather than the shared `Table`. That is a deliberate
 * departure from the house default for list surfaces: a request has no columns
 * worth aligning — it is one paragraph of prose plus two chips and a date — and
 * a data grid would truncate the one field that actually tells you which request
 * this is. The body IS the title here, because the intake deliberately does not
 * ask for a subject.
 */
export const SupportPage = ({ scope }: SupportPageProps) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const { data: permissions } = useSupportPermissions()
  const canViewCompany = permissions?.canViewCompanyRequests ?? false

  // Never request a scope the caller cannot have: the API 403s rather than silently narrowing,
  // so asking for it would surface as an error toast. The nav row for the company scope is
  // already disabled without the permission, so this only catches a typed-in URL.
  const effectiveScope = scope === 'company' && canViewCompany ? 'company' : 'mine'

  const {
    data: requests = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useSupportRequests(effectiveScope)

  const [filters, setFilters] = useState<SupportListFilters>(EMPTY_SUPPORT_FILTERS)
  const visible = useMemo(() => applySupportFilters(requests, filters), [requests, filters])

  /**
   * Whether the skeleton is allowed on screen — NOT the same question as `isLoading`.
   *
   * The list "flashed in" and layout shift was not the cause: the skeleton is footprint-exact
   * against the loaded page on both scopes, measured at zero delta on every region. What the
   * eye caught was the skeleton itself. This query answers in ~30ms locally, so the skeleton
   * mounted, painted a frame or two, and vanished. `useDeferredLoading` swallows that: nothing
   * is shown unless loading outlasts the grace period, and anything that IS shown stays long
   * enough to read as deliberate.
   */
  const showSkeleton = useDeferredLoading(isLoading)

  /**
   * How far down the sticky header pins.
   *
   * NOT 0, which is what every other page using `StickyFormHeader` passes. This page renders
   * inside the settings shell, whose own "Settings" header is ALREADY sticky at the top of the
   * scrollport — pinning at 0 would park this block underneath it, invisible but still covering
   * the list. And it cannot be a constant: the shell header grows by its back-link row (47px)
   * when a cross-surface referrer brought the user in, so the number differs between arriving
   * from the sidebar and arriving from the `?` shortcuts dialog. `SettingsPage` publishes the
   * height it already computes for its own sub-nav; this reads it rather than re-deriving it.
   * Outside the shell the context defaults to 0.
   */
  const stickyTop = useSettingsStickyOffset()

  return (
    <Box>
      {/*
        STICKY — title, "New request" and the Search/Filter/Sort row all stay pinned.

        The controls are the reason. A card list this long scrolls for several viewports, and a
        search box you have to scroll back up to reach is not doing its job: the moment you want
        to narrow the list is the moment you have already scrolled past the control that narrows
        it. So the toolbar rides INSIDE the sticky block (as `children`) rather than sitting in
        the flow beneath it — the same arrangement `/whats-new` and the long create/edit forms
        use, via the same component, so the fiddly parts (finding the real scroll parent — the
        app scrolls inside <main>, not the window; masking the scrollport's 24px top padding so
        cards are not seen sliding through the band above the title) stay solved once (§36).

        The edge is the component's hairline. This passed `edge="fade"` on the argument that
        one more horizontal rule over a column of bordered cards reads as another card edge —
        true as far as it goes, but the fade was invisible: page background and card background
        differ by 6-7 per channel, so the gradient dissolved near-black into near-black. Measurement
        the reason a taller gradient could not fix it are at the top of `StickyFormHeader`.
      */}
      <StickyFormHeader
        // Matches the left-nav label for this scope. The nav says "My requests" under Personal
        // and "Support" under Company, and a page whose title disagrees with the row that
        // selected it reads as the wrong page having loaded.
        title={effectiveScope === 'company' ? 'Support' : 'My requests'}
        // `nested`, because this Header sits UNDER the settings page's own "Settings" header.
        // Without it the component reserves a wayfinding row that never renders here, putting
        // this title 20px below where Appearance/Profile/Notifications put theirs — measured
        // 145.9px against their 125.9px. Every `selfTitled` settings section has the same gap
        // for the same reason; `nested` exists precisely to close it. It also sets the
        // title→controls gap to a nested header's own 12px gutter, which is what keeps this
        // page's rhythm identical to the non-sticky version it replaces.
        nested
        top={stickyTop}
        actions={
          <Button
            data-tour="support-new-request"
            variant="primary"
            // Matches the Header's enforced height, which is now 40 app-wide.
            size="medium"
            disableElevation
            startIcon={<PlusIcon size={16} strokeWidth={1} />}
            onClick={() => navigate(ROUTES.SUPPORT_NEW)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            New request
          </Button>
        }
      >
        {/* Controls render only when there is something to narrow. A search box over an empty
            list is furniture: it cannot help, and it pushes the empty state's explanation down
            the page behind a control the user has no reason to touch.

            The LOADING branch draws them as a skeleton IN THE SAME SLOT rather than letting the
            page skeleton draw a toolbar below the header. Now that the real toolbar lives inside
            the sticky block, a stand-in rendered anywhere else would occupy a different footprint
            — which is the whole failure §19 is about, and it would land on the first card of a
            list whose skeleton is currently pixel-exact.

            The middle arm — loading, but inside the grace period — renders NOTHING rather than
            falling through to the real toolbar. A toolbar over a list that has not arrived is
            a control with nothing to narrow, and it would pop in ahead of the cards it filters. */}
        {showSkeleton ? (
          <SupportListControlsSkeleton />
        ) : isLoading ? null : !isError && requests.length > 0 ? (
          <Fade in timeout={DEFERRED_LOADING_FADE_MS}>
            <Box>
              <SupportListControls value={filters} onChange={setFilters} />
            </Box>
          </Fade>
        ) : null}
      </StickyFormHeader>

      {showSkeleton ? (
        <SupportPageSkeleton scope={effectiveScope} />
      ) : isLoading ? null : (
        /* Opacity only, and legitimate ONLY because the skeleton's footprint matches this
           block's exactly — polish on a layout that does not move. A transform or a height
           animation here would put back the shift the footprint pass removed. */
        <Fade in timeout={DEFERRED_LOADING_FADE_MS}>
          <Stack data-tour="support-requests" spacing={4}>
            {/* Failure and emptiness are different answers and must not share a
                message — see QueryErrorState for why this is not cosmetic. */}
            {isError ? (
              <QueryErrorState
                error={error}
                subject={effectiveScope === 'company' ? "your company's requests" : 'your requests'}
                permissionLabel={
                  effectiveScope === 'company' ? 'View company requests' : undefined
                }
                onRetry={() => refetch()}
              />
            ) : requests.length === 0 ? (
              /* The two scopes get different empty copy. "When you contact support…" is only
                 true of your OWN list; on the company view the reason it is empty is that
                 nobody at the company has raised anything, and telling an admin what THEY
                 should do to fill it would be the wrong instruction. */
              // Centred in the pane, matching AddressBookPage's treatment. Unwrapped, an
              // EmptyState renders at its natural height at the TOP of the list column, which on
              // a tall empty page reads as content that failed to load rather than as a
              // deliberate empty state.
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '55vh' }}>
                <EmptyState
                  icon={<LifeBuoyIcon strokeWidth={1} />}
                  title={effectiveScope === 'company' ? 'No requests from your company yet' : 'No requests yet'}
                  text={
                    effectiveScope === 'company'
                      ? 'When anyone at your company contacts support, reports a bug or suggests a feature, it shows up here with its status.'
                      : 'When you contact support, report a bug or suggest a feature, it shows up here with its status.'
                  }
                />
              </Box>
            ) : visible.length === 0 ? (
              /* "Nothing matches" is a DIFFERENT answer from "nothing exists", and conflating them
                 is the bug this avoids: showing "No requests yet" to someone whose filters simply
                 exclude everything tells them their data is gone. This state names the filters as
                 the cause and offers the way out. */
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '55vh' }}>
                <EmptyState
                  icon={<SearchXIcon strokeWidth={1} />}
                  title="No requests match your filters"
                  text={`None of the ${requests.length} request${
                    requests.length === 1 ? '' : 's'
                  } here match what you have selected. Try a different search, or clear the filters.`}
                  buttons={
                    <Button
                      variant="secondary"
                      size="medium"
                      onClick={() => setFilters(EMPTY_SUPPORT_FILTERS)}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear filters
                    </Button>
                  }
                />
              </Box>
            ) : (
              visible.map((request: SupportRequest) => (
                <Card
                  key={request.id}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(ROUTES.SUPPORT_REQUEST(request.id))}
                  // The `:focus-visible` half of the rule below was unreachable: `Card`
                  // renders a plain div, which cannot take focus, so the selector had
                  // never once matched. Keyboard users got no hover cue AND no way to
                  // open a request from the list. Enter and Space both, and
                  // preventDefault so Space activates instead of scrolling the page.
                  onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    navigate(ROUTES.SUPPORT_REQUEST(request.id))
                  }}
                  sx={{
                    p: 4,
                    mb: 0,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease-in',
                    outline: 'none',
                    // Action row, not a selectable card — brand border + focus ring
                    // is the right pattern here (§34), since clicking navigates
                    // rather than leaving the card visibly selected.
                    '&:hover, &:focus-visible': {
                      borderColor: theme.palette.brand[600],
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.brand[600], 0.06)}`,
                    },
                  }}
                >
                  <Stack spacing={3}>
                    <Stack direction="row" spacing={3} alignItems="center">
                      {/* Type is a fixed CLASSIFICATION, status is a lifecycle STATE — the
                          badge/pill split is what stops the two reading as two states of one
                          thing. Same component the Type filter renders (§36). */}
                      <SupportTypeBadge type={request.type} />
                      {/* VRG-427: never let an automatic report read as something the
                          submitter wrote. */}
                      {request.isSystemGenerated && (
                        <SystemGeneratedBadge occurrenceCount={request.occurrenceCount} />
                      )}
                      {/*
                        ONE pill, showing the lifecycle that actually governs this
                        row: a promoted feature shows its BOARD status ("Planned",
                        "Building", "Shipped"), everything else shows its request
                        status. `displayStatusKey` is the single decision point,
                        shared with the filter and the sort — see the long note
                        above it in SupportListControls for why the two vocabularies
                        cannot simply be shown side by side.
                      */}
                      <StatusChip
                        statuses={SUPPORT_DISPLAY_STATUSES}
                        status={displayStatusKey(request)}
                        size="small"
                      />
                      <Typography variant="body4" color="text.secondary">
                        #{request.reference}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {request.messageCount > 0 && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <MessageSquareIcon size={13} strokeWidth={1.5} />
                          <Typography variant="body4" color="text.secondary">
                            {request.messageCount}
                          </Typography>
                        </Stack>
                      )}
                      <Typography variant="body4" color="text.secondary">
                        {relativeOrDateTime(request.createdAt)}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="body3"
                      color="text.primary"
                      sx={{
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {request.body}
                    </Typography>

                    {/* Guarded on the pair, not rendered unconditionally: this Stack is a child
                        of a `spacing={3}` Stack, so an EMPTY one would still contribute its 12px
                        top margin and push every row of a list with no meta line 12px taller than
                        its skeleton. */}
                    {((effectiveScope === 'company' && request.submitterName) ||
                      request.boardRequestCount > 1) && (
                      <Stack
                        direction="row"
                        spacing={3}
                        alignItems="center"
                        sx={{ flexWrap: 'wrap' }}
                      >
                        {effectiveScope === 'company' && request.submitterName && (
                          <Typography variant="body4" color="text.secondary">
                            Raised by {request.submitterName}
                          </Typography>
                        )}

                        {/*
                          HOW MANY PEOPLE ASKED — the visible consequence of
                          dedupe-at-intake, and the only place a submitter can see
                          that "add mine to this" did anything.

                          This replaced "On the company board", which named a page
                          that no longer exists (VRG-324 retires /support/requests).
                          Still a marker rather than a link, for the original
                          reason: this card is itself `role="link"`, so a second
                          navigation target inside it is both a click-target trap
                          and invalid ARIA — a link inside a link.

                          Guarded on > 1, not on `boardItemId`. "1 person asked" is
                          a statement about the row you are already reading, and the
                          fact that a staffer promoted it is Vera's bookkeeping,
                          not news to the submitter.
                        */}
                        {request.boardRequestCount > 1 && (
                          <Typography variant="body4" color="text.secondary">
                            {request.boardRequestCount} people asked for this
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Card>
              ))
            )}
          </Stack>
        </Fade>
      )}
    </Box>
  )
}
